import React, { useState, useEffect } from 'react';
import {
  IconButton, TextField, Dialog, DialogActions, DialogContent, DialogTitle,
  Button, Switch, FormControlLabel, CircularProgress, Box, Alert, Typography, Chip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { BASE_URL } from '../../components/CommonExport';
import Component from '../../components/Component.js';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { Link } from 'react-router-dom';

function getAuthHeaders() {
  const token = sessionStorage.getItem('sessionToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const InappNotification = () => {
  const [notifications, setNotifications] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchNotifications();
  }, []);

  async function fetchNotifications() {
    try {
      setLoading(true);
      setError('');
      const response = await fetch(`${BASE_URL}/admin/notification/inapp`, {
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      });
      const data = await response.json();
      setNotifications(data.data?.result || []);
    } catch (err) {
      setError('Failed to load notifications');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  const handleEditClick = (notification) => {
    setSelectedNotification({ ...notification });
    setIsCreating(false);
    setOpenModal(true);
  };

  const handleAddClick = () => {
    setSelectedNotification({
      title: '',
      text: '',
      critical: false,
      image: '',
      current_version: '',
      max_valid_version: '',
      logo_url: '',
      cta_title: '',
      cta_link: '',
    });
    setIsCreating(true);
    setOpenModal(true);
  };

  const handleClose = () => {
    setOpenModal(false);
    setSelectedNotification(null);
    setIsCreating(false);
  };

  const handleSave = async () => {
    if (!selectedNotification) return;
    setSaving(true);
    setError('');
    try {
      const body = {
        ...selectedNotification,
        isUpdate: !isCreating,
      };
      const response = await fetch(`${BASE_URL}/admin/notification/inapp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || data.error || 'Failed to save');
      }
      setSuccess(isCreating ? 'Notification created successfully' : 'Notification updated successfully');
      setTimeout(() => setSuccess(''), 3000);
      fetchNotifications();
      handleClose();
    } catch (err) {
      setError(err.message || 'Failed to save notification');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSelectedNotification(prev => ({ ...prev, [name]: value }));
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 60 },
    { field: 'title', headerName: 'Title', width: 180 },
    {
      field: 'critical', headerName: 'Critical', width: 80,
      renderCell: (params) => (
        <Chip label={params.value ? 'Yes' : 'No'} color={params.value ? 'error' : 'default'} size="small" />
      ),
    },
    { field: 'text', headerName: 'Description', width: 250 },
    { field: 'current_version', headerName: 'Version', width: 90 },
    { field: 'max_valid_version', headerName: 'Max Version', width: 100 },
    {
      field: 'logo_url', headerName: 'Logo', width: 80,
      renderCell: (params) => params.value ? (
        <img src={params.value} alt="" style={{ width: 30, height: 30, objectFit: 'cover', borderRadius: 4 }} />
      ) : '—',
    },
    { field: 'cta_title', headerName: 'CTA Title', width: 120 },
    { field: 'cta_link', headerName: 'CTA Link', width: 150 },
    {
      field: 'created_at', headerName: 'Created', width: 140,
      renderCell: (params) => params.value ? new Date(params.value).toLocaleString() : '—',
    },
    {
      field: 'actions', headerName: 'Actions', width: 80,
      renderCell: (params) => (
        <IconButton onClick={() => handleEditClick(params.row)} color="primary" size="small">
          <EditIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <IconButton style={{ background: '#FFFFFF' }} component={Link} to="/">
                      <ArrowBackIcon />
                    </IconButton>
                    <h3 style={{ marginLeft: '20px' }}>In-App Notifications</h3>
                  </div>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddClick}>
                    Add Notification
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {error && <Alert severity="error" sx={{ mx: 2, mb: 1 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mx: 2, mb: 1 }} onClose={() => setSuccess('')}>{success}</Alert>}

        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-body">
                <div style={{ height: 500, width: '100%' }}>
                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <DataGrid
                      rows={notifications}
                      columns={columns}
                      pageSize={10}
                      rowsPerPageOptions={[10, 25]}
                      disableSelectionOnClick
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Add / Edit Dialog */}
        <Dialog open={openModal} onClose={handleClose} fullWidth maxWidth="sm">
          <DialogTitle>{isCreating ? 'Add Notification' : 'Edit Notification'}</DialogTitle>
          <DialogContent>
            {selectedNotification && (
              <>
                <TextField label="Title" name="title" value={selectedNotification.title || ''} onChange={handleChange}
                  fullWidth margin="dense" required />
                <TextField label="Description" name="text" value={selectedNotification.text || ''} onChange={handleChange}
                  fullWidth margin="dense" multiline rows={3} required />
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(selectedNotification.critical)}
                      onChange={(e) => handleChange({ target: { name: 'critical', value: e.target.checked } })}
                      color="error"
                    />
                  }
                  label={<Typography variant="body2">Critical (force update)</Typography>}
                />
                {selectedNotification.critical && (
                  <>
                    <TextField label="Current App Version" name="current_version" value={selectedNotification.current_version || ''}
                      onChange={handleChange} fullWidth margin="dense" placeholder="e.g. 2.1.0" required />
                    <TextField label="Max Valid Version (users below this MUST update)" name="max_valid_version"
                      value={selectedNotification.max_valid_version || ''} onChange={handleChange}
                      fullWidth margin="dense" placeholder="e.g. 2.0.0" required />
                  </>
                )}
                <TextField label="Image URL" name="image" value={selectedNotification.image || ''} onChange={handleChange}
                  fullWidth margin="dense" placeholder="https://..." />
                <TextField label="Logo URL" name="logo_url" value={selectedNotification.logo_url || ''} onChange={handleChange}
                  fullWidth margin="dense" placeholder="https://..." />
                {selectedNotification.logo_url && (
                  <Box sx={{ mt: 1, mb: 1 }}>
                    <img src={selectedNotification.logo_url} alt="Logo preview" style={{ maxHeight: 50, borderRadius: 4 }}
                      onError={(e) => { e.target.style.display = 'none'; }} />
                  </Box>
                )}
                <TextField label="CTA Button Title" name="cta_title" value={selectedNotification.cta_title || ''}
                  onChange={handleChange} fullWidth margin="dense" placeholder="e.g. Update Now" />
                <TextField label="CTA Button Link" name="cta_link" value={selectedNotification.cta_link || ''}
                  onChange={handleChange} fullWidth margin="dense" placeholder="e.g. https://play.google.com/..." />
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} variant="contained" disabled={saving}>
              {saving ? <CircularProgress size={20} /> : (isCreating ? 'Create' : 'Save')}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </Component>
  );
};

export default InappNotification;
